import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { ApplicationHttpClient } from '../../components/shared/http/application-http-client';
import { Routes } from '../../config/routes';
import { TreasuryConversionStage } from '../../enums/treasury-conversion-stage.enum';

/** What the acceptance endpoint answers: it took it, or it refused and said why. */
export interface TreasuryConversionTermsResult {
    accepted: boolean;
    blocker: string | null;
}

export interface TreasuryConversionStageResponse {
    stage: TreasuryConversionStage;

    /**
     * Whether the organization's own admin has already accepted Stripe's Treasury terms
     * (SD-10777).
     *
     * ⚠️ A SEPARATE FACT FROM THE STAGE, not derivable from it. Stripe can hold an acceptance for
     * a while before the capability activates, so an organization is routinely accepted AND still
     * `awaiting_capability`. Deriving this from the stage would put the accept button back in
     * front of an admin whose acceptance is already on the record.
     */
    termsAccepted?: boolean;

    /** The Connect account this conversion acts on, for the screen to name. */
    connectAccountId?: string | null;

    /**
     * What Stripe currently says about that account (SD-10856).
     *
     * ⚠️ WITHOUT THIS THE PAGE CANNOT TELL THREE DIFFERENT SITUATIONS APART, and so it showed the
     * same spinner for all of them, indefinitely: waiting on Stripe, Stripe waiting on the admin,
     * and genuinely stalled. On QA staging an admin accepted the terms, Stripe asked for an
     * account representative, and nothing on this page said so or offered a way to give it.
     */
    stripe?: TreasuryConversionStripeSignal;
}

export interface TreasuryConversionStripeSignal {
    /** `active`, `pending`, `inactive`, `restricted`, or null when nothing is known. */
    capability: string | null;

    /** Stripe is waiting on the ADMIN, not the other way round. */
    requiresAction: boolean;

    currentlyDue: string[];

    pastDue: string[];

    disabledReason: string | null;

    /**
     * A Stripe-hosted link to finish what is outstanding, on the conversion's OWN account.
     *
     * Present only when something is actually due. Null is the normal case and must render no
     * button: one offered beside "waiting on Stripe" sends the admin into a flow that asks them
     * for nothing, which reads as broken in a different way.
     */
    actionUrl: string | null;
}

/**
 * What an organization's own screens show about its conversion (SD-10570).
 *
 * A SEPARATE REQUEST, not a field on the bank-accounts payload, and the reason is a shape
 * constraint rather than a preference: that endpoint returns a bare JSON array, so an
 * organization-level field would mean wrapping the top level and changing the response for every
 * existing consumer. The bar on this program is no impact to any organization, on Treasury or off
 * it.
 *
 * NOT read from the JWT, which would have been free. `claims.ff` is built once at token issue, so
 * a switchover completing mid-session would leave a logged-in user on pre-conversion screens while
 * their invoices and payouts have already moved. Per-request is the whole point.
 *
 * FETCHED EAGERLY AND CACHED PER ORGANIZATION so a screen never renders pre-conversion UI while
 * the answer is in flight. Showing the old surfaces briefly is a shortened version of exactly the
 * failure the stage exists to prevent.
 *
 * FAILS TO `NONE`, deliberately. Every surface keyed on this is ADDITIVE — the stage can only
 * reveal conversion-aware behaviour, never remove behaviour an organization has today — so a
 * failed fetch degrades to precisely the screens the organization sees now. The opposite default
 * would let one failed request hide an organization's bank accounts from it.
 */
@Injectable({providedIn: 'root'})
export class TreasuryConversionStageService {
    private readonly _stage$ = new BehaviorSubject<TreasuryConversionStage>(TreasuryConversionStage.NONE);

    /** The last stage fetched, for templates that want it synchronously. */
    stage: TreasuryConversionStage = TreasuryConversionStage.NONE;

    /** Whether the terms are already accepted, from the same fetch. */
    termsAccepted = false;

    /**
     * The same answer as a stream, for templates (SD-10856).
     *
     * ⚠️ CONVERSION-ONLY BY CONSTRUCTION. An organization that is not being converted never leaves
     * `NONE`, so this stays `false` for it for ever and every consumer is gated on
     * `conversionInProgress` as well. Nothing in the normal flow reads it.
     *
     * It exists because `termsAccepted` is a plain field: the page has to re-render the moment the
     * admin agrees, and a field cannot say so.
     */
    private readonly _termsAccepted$ = new BehaviorSubject<boolean>(false);

    /**
     * Whether the admin has FINISHED the KYC form, not merely agreed to the terms (SD-10856).
     *
     * ⚠️ THE DIFFERENCE IS THE WHOLE BUG. `termsAccepted` flips when they press "Agree and
     * continue", which is the moment the KYC modal OPENS. Gating the "we are moving your account"
     * banner on that swapped the page behind them while they were still filling the form in - and
     * since that banner has no button, closing the modal mid-KYC left them with no way back in.
     * Richard, on camera: "if I close this there's no way to re-open it back here, so this opened
     * prematurely."
     *
     * This flips only when the Stripe dialog closes having been completed - the Confirm press.
     * Until then the resume banner keeps its button.
     *
     * ⚠️ NOT PERSISTED, AND THAT IS DELIBERATE. A reload mid-KYC resets it to false, which gives
     * the admin the button back - exactly what they need, because a reload also closed the modal.
     */
    private readonly _kycSubmitted$ = new BehaviorSubject<boolean>(false);

    /** The Connect account the conversion acts on, from the same fetch. */
    connectAccountId: string | null = null;

    /** The organization the cached stage belongs to, so a switch cannot read the previous org's. */
    private cachedForOrganizationId: number | null = null;

    /**
     * The organization `_kycSubmitted$` belongs to.
     *
     * ⚠️ ITS OWN FIELD, NOT `cachedForOrganizationId` (review F-881f). The first cut of this guard
     * reused the cache marker, which is wrong because that marker carries a SECOND meaning: both
     * `acceptTerms()` and `reset()` null it to say "the cached stage is stale, re-fetch". Null
     * there means the cache was dropped, never "a different organization".
     *
     * The conversion flow nulls it at exactly the worst moment. `acceptTerms()` sets it to null,
     * nothing re-fetches before Stripe's form closes, and the completion poller's `timer(0, …)`
     * then forces a fetch whose `map` ran `null !== organizationId` and cleared the flag — so the
     * hold banner appeared and vanished again inside a second, dropping the admin back on the very
     * page this ticket exists to keep them off.
     */
    private kycSubmittedForOrganizationId: number | null = null;

    /**
     * What Stripe last said about this conversion's account, or null when nothing is known
     * (SD-10856). Null on every failure path, so a page that cannot reach the server shows what it
     * showed before rather than inventing a requirement.
     */
    stripe: TreasuryConversionStripeSignal | null = null;

    /** Whether the cached stage came from an answer rather than from a swallowed failure. */
    private answerIsReliable = false;

    constructor(private readonly _http: ApplicationHttpClient) {
    }

    /**
     * The last failure this service swallowed, for anything that wants to ask.
     *
     * Kept rather than logged: the fallback is deliberately silent on screen, and a caller that
     * genuinely needs to tell "not converted" from "could not ask" has somewhere to look.
     */
    lastError: unknown = null;

    get termsAccepted$(): Observable<boolean> {
        return this._termsAccepted$.asObservable();
    }

    get kycSubmitted$(): Observable<boolean> {
        return this._kycSubmitted$.asObservable();
    }

    /** The admin pressed Confirm and Stripe's form closed. {@see kycSubmitted$}. */
    markKycSubmitted(organizationId: number): void {
        this.kycSubmittedForOrganizationId = organizationId;
        this._kycSubmitted$.next(true);
    }

    get stage$(): Observable<TreasuryConversionStage> {
        return this._stage$.asObservable();
    }

    /**
     * Fetch the stage for an organization, reusing the cached value for that same organization.
     *
     * Cached per organization rather than globally: a super switching between organizations would
     * otherwise carry one organization's stage onto another's screens, which is the cross-tenant
     * version of the staleness this service exists to avoid.
     */
    fetch(organizationId: number, force: boolean = false): Observable<TreasuryConversionStage> {
        if (!force && this.cachedForOrganizationId === organizationId) {
            return of(this.stage);
        }

        return this._http
            .get<TreasuryConversionStageResponse>(
                // ⚠️ MakeLegFiCoreUrl PREPENDS THE CORE API BASE, and without it this resolves
                // against the PAGE origin and 404s. The catchError below then returns NONE, which
                // is indistinguishable from a genuine "not converted" — so every converted
                // organization silently keeps its pre-conversion menus forever. Every sibling call
                // in this route family wraps the path the same way (review F-cf2f).
                Routes.MakeLegFiCoreUrl(Routes.TreasuryModule.ConversionStage(organizationId)),
            )
            .pipe(
                // ⚠️ THE SIBLING FIELDS ARE CAPTURED HERE, IN THE MAP, and not in the `tap` below.
                // The tap only ever sees the mapped stage, and the catchError's fallback bypasses
                // this entirely — which is exactly what should happen, since a failed fetch must
                // leave the page on today's behaviour rather than claiming terms were accepted.
                map((response: TreasuryConversionStageResponse) => {
                    // ⚠️ SET ONLY HERE, ON THE SUCCESS PATH (review F-7dba). `catchError` below
                    // answers `none` so every surface degrades to today's screen, which is right —
                    // but it makes a FAILED fetch indistinguishable from "this organization is not
                    // being converted". For most readers that is harmless; for the one deciding
                    // whether to release a conversion hold it is the difference between waiting
                    // and handing an admin a flow that would undo the conversion.
                    this.answerIsReliable = true;
                    this.lastError = null;
                    this.termsAccepted = response?.termsAccepted === true;
                    this._termsAccepted$.next(this.termsAccepted);

                    // ⚠️ CLEARED WHENEVER THE ANSWER IS FOR A DIFFERENT ORGANIZATION (review
                    // F-aa7e). `_kycSubmitted$` records that THIS admin finished THIS
                    // organization's Stripe form in THIS session. The service is a root singleton
                    // and an organization switch is in-app navigation, so without this a super
                    // admin who completed one conversion and switched to a second one still being
                    // converted would carry the flag across - and the second organization's banner
                    // and its "Complete verification" button would be hidden, with no way back in.
                    //
                    // The same reasoning that `cachedForOrganizationId` and
                    // `isConversionInProgressFor()` already exist for.
                    if (this.kycSubmittedForOrganizationId !== null
                        && this.kycSubmittedForOrganizationId !== organizationId) {
                        this.kycSubmittedForOrganizationId = null;
                        this._kycSubmitted$.next(false);
                    }
                    this.connectAccountId = response?.connectAccountId ?? null;
                    // Captured here for the same reason its siblings are: the `tap` sees only the
                    // mapped stage, and a failed fetch must leave the page on today's behaviour
                    // rather than claiming Stripe said something.
                    this.stripe = response?.stripe ?? null;

                    return response?.stage ?? TreasuryConversionStage.NONE;
                }),
                catchError((error: unknown) => {
                    // LOUD ABOUT A SILENT DEGRADATION. The fallback below is correct — every
                    // surface keyed on this is additive, so NONE is exactly today's screen — but
                    // correct and invisible is its own defect: if this endpoint starts failing,
                    // every CONVERTED organization quietly gets its pre-conversion menu back,
                    // including Add Deposit Account, and nobody learns.
                    //
                    // The harm is bounded, which is why this does not redesign anything. Both
                    // deposit-account refusals were widened server-side for converted
                    // organizations, so a user who acts on the restored menu is refused by the
                    // API. It fails safe at both layers.
                    //
                    // ⚠️ AND IT IS NOT A `console.error` (review F-2ebd). A console line in
                    // shipped UI code is read by no monitoring and is actionable by no user; it
                    // reads as diagnostics while being neither. The failure that matters is the
                    // 404 itself, which the API layer already records, and the fallback is safe —
                    // so the honest thing here is to degrade quietly rather than to write
                    // somewhere nobody looks.
                    this.lastError = error;
                    this.answerIsReliable = false;

                    // FAILS TO "NOT IN CONVERSION, NOTHING ACCEPTED", which is today's page.
                    this.termsAccepted = false;
                    this._termsAccepted$.next(false);
                    this.kycSubmittedForOrganizationId = null;
                    this._kycSubmitted$.next(false);
                    this.connectAccountId = null;

                    return of(TreasuryConversionStage.NONE);
                }),
                tap((stage: TreasuryConversionStage) => {
                    this.stage = stage;
                    this.cachedForOrganizationId = organizationId;
                    this._stage$.next(stage);
                }),
            );
    }

    /**
     * The organization is mid-conversion and waiting on Stripe to approve the capability.
     *
     * The EARLIEST of the surfaces, and the one that fails worse LATE: it is what tells an
     * association to finish its business details. Keyed on the switchover instead, it would only
     * appear once it was useless.
     */
    isAwaitingCapability(): boolean {
        return this.stage === TreasuryConversionStage.AWAITING_CAPABILITY;
    }

    /**
     * There is a Treasury financial account to show.
     *
     * Keyed on provisioning, NOT on the conversion existing, and it fails worse EARLY: an empty
     * Treasury balance shown to an association before there is anything in it reads as "my money
     * is gone".
     */
    hasFinancialAccount(): boolean {
        return this.stage === TreasuryConversionStage.PROVISIONED
            || this.stage === TreasuryConversionStage.CONVERTED;
    }

    /**
     * The switchover has run and invoices point at the Treasury account.
     *
     * The LATEST key, and the dangerous one to fire early. Until the switchover runs, live
     * invoices still point at the organization's old deposit accounts, so hiding those accounts at
     * mark time would remove both sight of and the ability to manage the accounts its real money
     * is using.
     */
    isConverted(): boolean {
        return this.stage === TreasuryConversionStage.CONVERTED;
    }

    /**
     * ⚠️ THE ONE PREDICATE THE WHOLE PAGE READS (SD-10777). Is this organization mid-conversion,
     * and therefore inert?
     *
     * Richard, 2026-10-05: "if the org is not on treasury conversion, it follows the existing
     * path, but if it is on the conversion path, it must ONLY allow the tos to be accepted."
     *
     * ⚠️ `NONE` MEANS NORMAL, NEVER INERT, and getting that backwards would make every ordinary
     * Treasury onboarding inert. The resolver answers `none` for an organization with no
     * conversion row, a native row, or an unmarked one — which is every organization that is not
     * being converted, including the 275 already on Treasury.
     *
     * ⚠️ AND `CONVERTED` MEANS NORMAL TOO. Richard: once the conversion completes they get full
     * access, no lingering restriction. The window closes by itself.
     *
     * So inert is the two middle stages and nothing else.
     */
    isConversionInProgress(): boolean {
        return this.stage === TreasuryConversionStage.AWAITING_CAPABILITY
            || this.stage === TreasuryConversionStage.PROVISIONED;
    }

    /**
     * ⚠️ THE SAME QUESTION, ASKED ABOUT A NAMED ORGANIZATION (review F-c577).
     *
     * `fetch()` guards its cache with `cachedForOrganizationId` precisely so a switch cannot read
     * the previous organization's answer — but that guard only runs inside `fetch()`. Every reader
     * that touches the bare `stage` field synchronously bypasses it, and an organization switch is
     * IN-APP navigation rather than a page reload, so this root singleton keeps the previous
     * organization's stage across it.
     *
     * The consequence was specific: a super leaving an association mid-conversion for one that is
     * not would see the second association offered the conversion surfaces belonging to the first.
     *
     * Unknown answers FALSE, which is the safe direction here: every surface keyed on this is
     * additive, so "we have not asked about this organization yet" renders exactly today's page
     * rather than inventing a conversion for an organization that has none.
     */
    isConversionInProgressFor(organizationId: number): boolean {
        if (this.cachedForOrganizationId !== organizationId) {
            return false;
        }

        return this.isConversionInProgress();
    }

    /**
     * Whether we hold a RELIABLE answer for this organization — one the server actually gave.
     *
     * ⚠️ NOT THE SAME AS "we have a cached value" (review F-7dba). A failed fetch still populates
     * the cache, with `none`, deliberately — so a caller that must not mistake a network failure
     * for a finished conversion has to ask this instead.
     */
    hasAnswerFor(organizationId: number): boolean {
        return this.cachedForOrganizationId === organizationId && this.answerIsReliable;
    }

    /** Whether the organization's own admin has already accepted Stripe's Treasury terms. */
    hasAcceptedTerms(): boolean {
        return this.termsAccepted;
    }

    /**
     * The one live control during the inert window: accept the terms.
     *
     * False once they have accepted, because there is nothing left for them to do — the
     * conversion is then waiting on Stripe and on us, not on them.
     */
    canAcceptTerms(): boolean {
        return this.isConversionInProgress() && ! this.termsAccepted;
    }

    /**
     * Accept Stripe's Treasury terms for this organization's conversion (SD-10777).
     *
     * ⚠️ THE ACCOUNT IS NOT PASSED. The server resolves it from the conversion's own record,
     * because a client-supplied Connect account id is a way to accept terms against somebody
     * else's Stripe account. The screen knows which account it is — `connectAccountId` above —
     * only so it can name it.
     *
     * On success the cache is dropped rather than patched, so the next read comes from the server
     * and the page cannot sit on a stale "not accepted".
     */
    acceptTerms(organizationId: number): Observable<TreasuryConversionTermsResult> {
        return this._http
            .post<TreasuryConversionTermsResult>(
                Routes.MakeLegFiCoreUrl(Routes.TreasuryModule.ConversionTerms(organizationId)),
                // `ApplicationHttpClient.post` takes a STRING body, so the empty object is
                // serialised here rather than passed raw. There is nothing to send: the server
                // resolves the organization from the path and the account from the conversion.
                JSON.stringify({}),
            )
            .pipe(
                // ⚠️ THE BLOCKER IS CARRIED, NOT DISCARDED (review F-3acb). This used to map the
                // response down to a bare boolean, so a server refusal and a success were the same
                // value by the time anything could act on them — the card closed, a spinner ran
                // for a minute and the admin was thanked for an agreement that was never recorded.
                // The server says WHY it refused; throwing that away is what made a blocked
                // conversion look finished.
                map((response): TreasuryConversionTermsResult => ({
                    accepted: response?.accepted === true,
                    blocker: response?.blocker ?? null,
                })),
                tap((result: TreasuryConversionTermsResult) => {
                    if (result.accepted) {
                        this.termsAccepted = true;
                        this._termsAccepted$.next(true);
                        this.cachedForOrganizationId = null;
                    }
                }),
            );
    }

    /** Drop the cache. For an organization switch, or after a conversion action. */
    reset(): void {
        this.stage = TreasuryConversionStage.NONE;
        this.termsAccepted = false;
        this.connectAccountId = null;
        this.cachedForOrganizationId = null;
        this._stage$.next(TreasuryConversionStage.NONE);
        this._termsAccepted$.next(false);
        // Reset means "forget this organization entirely", so the in-session KYC flag goes too
        // (review F-aa7e). Leaving it set would carry one organization's progress into the next.
        this.kycSubmittedForOrganizationId = null;
        this._kycSubmitted$.next(false);
    }
}
